/**
 * UploadFingerCard — one finger × three side slots (F/L/R).
 *
 * Each slot:
 *   - empty: clickable + drag-droppable file zone.
 *   - selected (uploading): thumbnail + spinner.
 *   - uploaded: thumbnail + green checkmark.
 *   - error: red icon + retry hint.
 *
 * After 3 sides uploaded the backend automatically enqueues the stitch job
 * (see apps/api/src/routes/upload.ts line ~84). Parent (Layer4Page) polls
 * /api/klien/:id/analysis and replaces this card with a FingerCard once
 * the analysis row materializes.
 *
 * Endpoint: POST /api/upload/:klien_id/finger/:finger/side/:side
 *   - multipart formData with field name "file"
 *   - MIME allowed: image/jpeg | image/jpg | image/png
 *   - Returns: { upload_id, storage_path, stitch_job_enqueued }
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Upload, X, Loader2, SkipForward, Play, Ban } from "lucide-react";
import type { FingerKey } from "@dfa/types";
import { auth, apiPost } from "../api.js";
import { apiUrl } from "../lib/apiUrl.js";
import { Button, Card } from "./ui/index.js";

type Side = "F" | "L" | "R";

const SIDES: { key: Side; label: string; hint: string }[] = [
  { key: "F", label: "Depan", hint: "F" },
  { key: "L", label: "Samping Kiri", hint: "L" },
  { key: "R", label: "Samping Kanan", hint: "R" },
];

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB safety cap
const ACCEPTED_MIME = /^image\/(jpe?g|png)$/;

interface UploadFingerCardProps {
  klienId: string;
  finger: FingerKey;
  fingerLabel: string;
  /** Sides already uploaded server-side (from API /analysis upload_progress). */
  sidesUploaded: Side[];
  /** Called after EACH successful upload — parent should refetch analysis. */
  onUploaded: () => void;
  disabled?: boolean;
}

// Opsi X: "skipped" = operator chose to not upload this side (client-only state,
// no DB column). A finger needs >= 1 uploaded side; skipped sides are not "missing".
type SlotStatus = "empty" | "uploading" | "uploaded" | "error" | "skipped";

interface SlotState {
  status: SlotStatus;
  thumbnailUrl?: string;
  error?: string;
}

export function UploadFingerCard({
  klienId,
  finger,
  fingerLabel,
  sidesUploaded,
  onUploaded,
  disabled,
}: UploadFingerCardProps) {
  // Initial state derives from server-known uploads.
  const [slots, setSlots] = useState<Record<Side, SlotState>>(() => ({
    F: { status: sidesUploaded.includes("F") ? "uploaded" : "empty" },
    L: { status: sidesUploaded.includes("L") ? "uploaded" : "empty" },
    R: { status: sidesUploaded.includes("R") ? "uploaded" : "empty" },
  }));

  // Re-sync when server-side state changes (e.g. after refetch).
  useEffect(() => {
    setSlots((prev) => {
      const next = { ...prev };
      for (const side of ["F", "L", "R"] as Side[]) {
        if (sidesUploaded.includes(side) && next[side].status !== "uploaded") {
          next[side] = { status: "uploaded" };
        }
      }
      return next;
    });
  }, [sidesUploaded.join(",")]);

  // Cleanup blob URLs on unmount.
  useEffect(() => {
    return () => {
      for (const s of Object.values(slots)) {
        if (s.thumbnailUrl) URL.revokeObjectURL(s.thumbnailUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function uploadFile(side: Side, file: File) {
    if (!ACCEPTED_MIME.test(file.type)) {
      setSlots((prev) => ({ ...prev, [side]: { status: "error", error: "Hanya JPEG / PNG" } }));
      return;
    }
    if (file.size > MAX_BYTES) {
      setSlots((prev) => ({ ...prev, [side]: { status: "error", error: `Max ${MAX_BYTES / 1024 / 1024} MB` } }));
      return;
    }

    const thumbnailUrl = URL.createObjectURL(file);
    setSlots((prev) => ({ ...prev, [side]: { status: "uploading", thumbnailUrl } }));

    try {
      const token = auth.getToken();
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(apiUrl(`/api/upload/${klienId}/finger/${finger}/side/${side}`), {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          msg = body?.error?.message ?? msg;
        } catch { /* keep status */ }
        setSlots((prev) => {
          const old = prev[side];
          if (old.thumbnailUrl) URL.revokeObjectURL(old.thumbnailUrl);
          return { ...prev, [side]: { status: "error", error: msg } };
        });
        return;
      }
      // Success — keep thumbnail visible with checkmark.
      setSlots((prev) => ({ ...prev, [side]: { status: "uploaded", thumbnailUrl } }));
      onUploaded();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSlots((prev) => {
        const old = prev[side];
        if (old.thumbnailUrl) URL.revokeObjectURL(old.thumbnailUrl);
        return { ...prev, [side]: { status: "error", error: msg } };
      });
    }
  }

  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [showCacatConfirm, setShowCacatConfirm] = useState(false);
  const [markingCacat, setMarkingCacat] = useState(false);

  const completedCount = (["F", "L", "R"] as Side[]).filter((s) => slots[s].status === "uploaded").length;
  const skippedCount = (["F", "L", "R"] as Side[]).filter((s) => slots[s].status === "skipped").length;
  const fUploaded = slots.F.status === "uploaded";
  const canProcess = fUploaded && !disabled; // F (depan) WAJIB; L/R opsional

  function setSlot(side: Side, status: SlotStatus) {
    setSlots((prev) => {
      const old = prev[side];
      if (old.thumbnailUrl && status !== "uploaded") URL.revokeObjectURL(old.thumbnailUrl);
      return { ...prev, [side]: { status } };
    });
  }

  async function processFinger() {
    if (!canProcess) return;
    setProcessing(true);
    setProcessError(null);
    try {
      await apiPost(`api/upload/${klienId}/finger/${finger}/process`);
      onUploaded(); // parent refetches → analysis row appears → card swaps to staged/FingerCard
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : "Gagal memproses");
      setProcessing(false);
    }
  }

  async function markCacat() {
    setMarkingCacat(true);
    setProcessError(null);
    try {
      await apiPost(`api/upload/${klienId}/finger/${finger}/cacat`);
      onUploaded(); // parent refetches → cacat row → card swaps to DefectiveFingerCard
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : "Gagal menandai cacat");
      setMarkingCacat(false);
      setShowCacatConfirm(false);
    }
  }

  return (
    <Card animate accent={completedCount > 0 ? "info" : "none"}>
      <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-sm text-brand-900">{fingerLabel}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {completedCount} sisi ter-upload
            {skippedCount > 0 && ` · ${skippedCount} dilewati`}
            {!fUploaded && <span className="text-amber-700"> · Sisi depan (F) wajib</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {processError && <span className="text-[10px] text-tabrakan max-w-[140px]">{processError}</span>}
          {!disabled && (
            <button
              type="button"
              onClick={() => setShowCacatConfirm(true)}
              className="text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
              title="Jari cacat / tidak ada"
            >
              <Ban size={13} /> Tandai cacat
            </button>
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={!canProcess || processing}
            onClick={processFinger}
            iconLeft={processing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} strokeWidth={2.5} />}
          >
            {processing ? "Memproses…" : "Proses jari ini"}
          </Button>
        </div>
      </div>

      {/* Konfirmasi ringan tandai cacat (lebih ringan dari dialog hapus klien). */}
      {showCacatConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !markingCacat && setShowCacatConfirm(false)}>
          <div className="card w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-semibold text-slate-900">Tandai cacat?</h2>
            <p className="text-sm text-slate-600">
              Tandai <strong>{fingerLabel}</strong> sebagai cacat / tidak ada? Slot upload jari ini akan dinonaktifkan.
              Bisa dibatalkan nanti.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" disabled={markingCacat} onClick={() => setShowCacatConfirm(false)}>Batal</Button>
              <Button variant="primary" size="sm" disabled={markingCacat} onClick={markCacat}>
                {markingCacat ? "Menandai…" : "Ya, tandai cacat"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Layer 4 sekarang 1-jari-per-baris (full row). Limit slot max-width
          supaya thumbnail tidak meledak besar di layar lebar. */}
      <div className="grid grid-cols-3 gap-2 max-w-2xl">
        {SIDES.map(({ key, label, hint }) => (
          <SideSlot
            key={key}
            side={key}
            label={label}
            hint={hint}
            state={slots[key]}
            disabled={disabled || slots[key].status === "uploading"}
            onFile={(file) => uploadFile(key, file)}
            onClear={() => setSlot(key, "empty")}
            onSkip={() => setSlot(key, "skipped")}
            onUnskip={() => setSlot(key, "empty")}
          />
        ))}
      </div>
    </Card>
  );
}

interface SideSlotProps {
  side: Side;
  label: string;
  hint: string;
  state: SlotState;
  disabled?: boolean;
  onFile: (file: File) => void;
  onClear: () => void;
  onSkip: () => void;
  onUnskip: () => void;
}

function SideSlot({ side: _side, label, hint, state, disabled, onFile, onClear, onSkip, onUnskip }: SideSlotProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  function handleClick() {
    if (disabled || state.status === "uploading" || state.status === "skipped") return;
    inputRef.current?.click();
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled || state.status === "uploading") return;
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }

  // Skipped: muted box with an "undo" — not counted as missing.
  if (state.status === "skipped") {
    return (
      <div className="space-y-1">
        <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide text-center">
          {hint} <span className="normal-case font-normal">· {label}</span>
        </div>
        <div className="relative aspect-square rounded-lg border-2 border-dashed border-surface-3 bg-surface-2 flex flex-col items-center justify-center text-slate-400 gap-1">
          <SkipForward size={16} strokeWidth={1.8} />
          <span className="text-[10px]">Dilewati</span>
          {!disabled && (
            <button type="button" onClick={onUnskip} className="text-[9px] underline hover:text-brand-600 mt-0.5">
              batal lewati
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="text-[10px] font-medium text-slate-600 uppercase tracking-wide text-center">
        {hint} <span className="text-slate-400 normal-case font-normal">· {label}</span>
      </div>
      <div
        onClick={handleClick}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && state.status !== "uploading" && state.status !== "uploaded") setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={`relative aspect-square rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden transition-all ${
          disabled
            ? "border-surface-3 bg-surface-2 cursor-not-allowed"
            : state.status === "uploaded"
              ? "border-emerald-300 bg-emerald-50/30 cursor-default"
              : state.status === "uploading"
                ? "border-brand-300 bg-brand-50/30 cursor-wait"
                : state.status === "error"
                  ? "border-red-300 bg-red-50/30 cursor-pointer hover:border-red-400"
                  : isDragOver
                    ? "border-brand-500 bg-brand-50 cursor-copy"
                    : "border-surface-3 bg-surface-1 cursor-pointer hover:border-brand-300 hover:bg-brand-50/40"
        }`}
        role="button"
        aria-label={`Upload sisi ${hint}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = ""; // reset so the same file can be re-selected
          }}
        />

        {state.thumbnailUrl && (
          <img src={state.thumbnailUrl} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
        )}

        <AnimatePresence>
          {state.status === "empty" && (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-1 text-slate-400"
            >
              <Upload size={18} strokeWidth={1.8} />
              <span className="text-[10px]">klik / drop</span>
            </motion.div>
          )}
          {state.status === "uploading" && (
            <motion.div
              key="uploading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/30 flex items-center justify-center"
            >
              <span className="w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            </motion.div>
          )}
          {state.status === "uploaded" && (
            <motion.div
              key="uploaded"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 18 }}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow"
              aria-hidden
            >
              <Check size={12} strokeWidth={3.5} className="text-white" />
            </motion.div>
          )}
          {state.status === "error" && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-red-900/60 flex flex-col items-center justify-center text-white p-1 text-center"
            >
              <X size={18} strokeWidth={2.5} />
              <span className="text-[9px] mt-0.5 leading-tight">{state.error ?? "Gagal"}</span>
              <span className="text-[9px] underline mt-1">klik ulang</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Clear button (only when uploaded & re-upload allowed). */}
        {state.status === "uploaded" && !disabled && (
          <button
            type="button"
            className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white/90 hover:bg-white border border-slate-200 flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            title="Re-upload"
            aria-label="Re-upload sisi ini"
          >
            <X size={10} className="text-slate-600" />
          </button>
        )}
      </div>
      {/* Opsi X: skip this side (only when still empty). F (depan) WAJIB → no skip. */}
      {_side !== "F" && (state.status === "empty" || state.status === "error") && !disabled && (
        <button
          type="button"
          onClick={onSkip}
          className="w-full text-[9px] text-slate-400 hover:text-slate-600 inline-flex items-center justify-center gap-1"
        >
          <SkipForward size={10} /> Lewati sisi ini
        </button>
      )}
    </div>
  );
}
